import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const CGoto = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Control Flow</div>
                <h1>Goto & Labels</h1>
                <p className="hero-lead">
                    Understand goto statements and labels in C - how they work, why they're controversial, rare legitimate
                    uses (error handling, breaking nested loops), and why structured alternatives are usually better.
                </p>
            </div>

            <div className="content-section">
                <h2>Understanding Goto</h2>
                <p>
                    The goto statement unconditionally transfers control to a labeled statement elsewhere in the same
                    function. It's C's most primitive control flow mechanism, predating structured programming constructs
                    like if, while, and for. Goto has a notorious reputation - Dijkstra's famous 1968 paper "Go To Statement
                    Considered Harmful" sparked decades of debate about its use in programming.
                </p>
                <p>
                    Modern consensus: avoid goto in most code. Structured constructs (loops, if-else, functions) produce
                    clearer, more maintainable code. However, goto has legitimate uses in C for error handling with cleanup
                    code and breaking out of deeply nested loops. Understanding goto helps you recognize when it's actually
                    the clearest solution and when it's code smell.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`#include <stdio.h>

int main(void) {
    int x = 0;
    
    printf("Start\\n");
    
    if (x == 0) {
        goto skip;  // Jump to 'skip' label
    }
    
    printf("This is skipped\\n");
    
skip:  // Label (ends with colon)
    printf("End\\n");
    
    return 0;
}

/* Output:
Start
End
*/`} />
                </div>

                <ProTip><ul>
                        <li>Can only jump within the same function</li>
                        <li>Cannot jump into a block from outside it</li>
                        <li>Cannot jump over variable initializations</li>
                        <li>Label must be unique within function</li>
                        <li>Label followed by colon (:)</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>Why Goto Is Problematic</h2>
                <p>
                    Unrestricted goto creates "spaghetti code" - tangled control flow that's nearly impossible to follow.
                    When code can jump anywhere, understanding program state becomes difficult. Debugging requires tracing
                    every possible path. Maintenance is risky because changes might affect unexpected code paths.Structured
                    programming emerged specifically to address these problems.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Bad: Spaghetti code with goto */
int process_data(void) {
    int result = 0;
    
start:
    printf("Processing...\\n");
    result++;
    
    if (result < 5) goto start;
    if (result == 5) goto middle;
    if (result &gt; 10) goto end;
    
middle:
    printf("Middle\\n");
    result += 2;
    goto start;
    
end:
    return result;
}
// This is confusing! What's the execution flow?

/* Good: Structured version (clear and maintainable) */
int process_data_structured(void) {
    int result = 0;
    
    while (result < 10) {
        printf("Processing...\\n");
        result++;
        
        if (result == 5) {
            printf("Middle\\n");
            result += 2;
        }
    }
    
    return result;
}

/* Bad: Jumping backward creates loop-like structure */
int count = 0;
loop:
    printf("%d ", count);
    count++;
    if (count < 10) goto loop;
printf("\\n");

/* Good: Use actual loop */
for (int count = 0; count < 10; count++) {
    printf("%d ", count);
}
printf("\\n");

/* Bad: Jumping over variable initialization */
goto skip_init;

int x = 10;  // Jumped over!

skip_init:
    printf("%d\\n", x);  // Undefined behavior!

/* Bad: Complex control flow */
if (condition1) goto label1;
if (condition2) goto label2;
goto label3;

label1:
    // Code
    goto end;
    
label2:
    // Code
    goto label1;  // Jump backward!
    
label3:
    // Code
    
end:
    return;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Legitimate Uses of Goto</h2>
                <p>
                    Despite its bad reputation, goto has legitimate uses in C. The Linux kernel and many C projects use
                    goto for specific patterns where it's the clearest solution. These patterns are well-established
                    idioms that experienced C programmers recognize and accept as appropriate uses.
                </p>

                <h3>Use Case 1: Error Handling with Cleanup</h3>
                <p>
                    The most common legitimate use: centralizing cleanup code. When multiple error conditions require
                    the same cleanup (closing files, freeing memory), goto eliminates code duplication and ensures
                    cleanup always happens. This pattern appears throughout the Linux kernel.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Without goto (duplicated cleanup) */
int process_file(const char *filename) {
    FILE *fp = fopen(filename, "r");
    if (fp == NULL) {
        return -1;
    }
    
    char *buffer = malloc(1024);
    if (buffer == NULL) {
        fclose(fp);  // Cleanup duplicated
        return -1;
    }
    
    int *data = malloc(sizeof(int) * 100);
    if (data == NULL) {
        free(buffer);  // Cleanup duplicated
        fclose(fp);    // Cleanup duplicated
        return -1;
    }
    
    // Process file...
    
    if (processing_error) {
        free(data);    // Cleanup duplicated
        free(buffer);  // Cleanup duplicated
        fclose(fp);    // Cleanup duplicated
        return -1;
    }
    
    // Success cleanup
    free(data);
    free(buffer);
    fclose(fp);
    return 0;
}

/* With goto (centralized cleanup) */
int process_file_goto(const char *filename) {
    FILE *fp = NULL;
    char *buffer = NULL;
    int *data = NULL;
    int result = -1;
    
    fp = fopen(filename, "r");
    if (fp == NULL) {
        goto cleanup;
    }
    
    buffer = malloc(1024);
    if (buffer == NULL) {
        goto cleanup;
    }
    
    data = malloc(sizeof(int) * 100);
    if (data == NULL) {
        goto cleanup;
    }
    
    // Process file...
    
    if (processing_error) {
        goto cleanup;
    }
    
    result = 0;  // Success
    
cleanup:
    free(data);     // Safe even if NULL
    free(buffer);   // Safe even if NULL
    if (fp) fclose(fp);
    return result;
}

/* Real-world example */
int initialize_system(void) {
    int result = -1;
    
    if (!init_module_a()) {
        fprintf(stderr, "Failed to init module A\\n");
        goto error;
    }
    
    if (!init_module_b()) {
        fprintf(stderr, "Failed to init module B\\n");
        goto error_module_a;
    }
    
    if (!init_module_c()) {
        fprintf(stderr, "Failed to init module C\\n");
        goto error_module_b;
    }
    
    result = 0;  // Success
    return result;
    
error_module_b:
    cleanup_module_b();
error_module_a:
    cleanup_module_a();
error:
    return result;
}`} />
                </div>

                <h3>Use Case 2: Breaking Nested Loops</h3>
                <p>
                    Breaking out of deeply nested loops is awkward in C - break only exits the innermost loop. Goto
                    provides a clean way to exit multiple loop levels at once without flag variables or function extraction.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Without goto (awkward flag variable) */
int found = 0;

for (int i = 0; i < rows && !found; i++) {
    for (int j = 0; j < cols && !found; j++) {
        if (matrix[i][j] == target) {
            printf("Found at (%d, %d)\\n", i, j);
            found = 1;
        }
    }
}

/* With goto (cleaner) */
for (int i = 0; i < rows; i++) {
    for (int j = 0; j < cols; j++) {
        if (matrix[i][j] == target) {
            printf("Found at (%d, %d)\\n", i, j);
            goto found;
        }
    }
}

printf("Not found\\n");
found:
    // Continue here

/* Triple nested loop */
for (int x = 0; x < SIZE_X; x++) {
    for (int y = 0; y < SIZE_Y; y++) {
        for (int z = 0; z < SIZE_Z; z++) {
            if (cube[x][y][z] == target) {
                printf("Found at (%d,%d,%d)\\n", x, y, z);
                goto found_target;
            }
        }
    }
}

printf("Not found\\n");
return -1;

found_target:
    printf("Processing target...\\n");
    return 0;`} />
                </div>

                <h3>Use Case 3: State Machine Implementation</h3>
                <p>
                    Computed goto (non-standard but supported by GCC) can implement efficient state machines. This is
                    an advanced technique primarily used in interpreters and VMs for dispatch optimization.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Simple state machine with goto */
enum State { START, PROCESSING, DONE, ERROR };
enum State state = START;

state_machine:
    switch (state) {
        case START:
            printf("Starting...\\n");
            if (init_ok) {
                state = PROCESSING;
                goto state_machine;
            } else {
                state = ERROR;
                goto state_machine;
            }
            
        case PROCESSING:
            printf("Processing...\\n");
            process_data();
            state = DONE;
            goto state_machine;
            
        case DONE:
            printf("Done\\n");
            break;
            
        case ERROR:
            printf("Error\\n");
            break;
    }`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Alternatives to Goto</h2>
                <p>
                    Most uses of goto can be replaced with structured alternatives. Before using goto, consider these
                    approaches. They usually produce clearer code except for the specific legitimate use cases.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Alternative 1: Use functions */
// Instead of goto for cleanup:
int process(void) {
    if (error1) goto cleanup;
    if (error2) goto cleanup;
cleanup:
    // cleanup code
}

// Better: Extract to function
int do_work(void) {
    if (error1) return -1;
    if (error2) return -1;
    return 0;
}

int process_with_cleanup(void) {
    int result = do_work();
    // cleanup code always runs
    return result;
}

/* Alternative 2: Use break with flags */
// Instead of goto to break nested loops:
int found = 0;
for (int i = 0; i < n && !found; i++) {
    for (int j = 0; j < m; j++) {
        if (condition) {
            found = 1;
            break;
        }
    }
}

/* Alternative 3: Early return */
// Instead of goto end:
int process(void) {
    if (condition1) {
        return handle_case1();
    }
    
    if (condition2) {
        return handle_case2();
    }
    
    return handle_default();
}

/* Alternative 4: do-while(0) idiom */
// For cleanup with error handling
#define CLEANUP_ON_ERROR() \\
    do { \\
        if (error_condition) { \\
            cleanup(); \\
            return -1; \\
        } \\
    } while(0)

int process(void) {
    init();
    CLEANUP_ON_ERROR();
    
    work();
    CLEANUP_ON_ERROR();
    
    cleanup();
    return 0;
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <p>
                    If you must use goto, follow these guidelines to minimize problems. Even legitimate goto usage should
                    be clear, documented, and follow established patterns that other programmers will recognize.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Best Practice 1: Only jump forward and down */
// Good: Jump to cleanup at end
if (error) {
    goto cleanup;
}

// More code...

cleanup:
    // Cleanup here
    return;

// Bad: Jump backward (creates loop)
retry:
    if (!success) goto retry;  // Use while loop instead!

/* Best Practice 2: Use descriptive label names */
// Good:
goto error_cleanup;
goto found_target;
goto end_processing;

// Bad:
goto label1;
goto skip;
goto x;

/* Best Practice 3: Document why goto is used */
// Breaking out of nested loops
for (...) {
    for (...) {
        if (found) {
            goto search_complete;  // Exit both loops
        }
    }
}
search_complete:
    // Continue here

/* Best Practice 4: Keep goto scope small */
// Good: goto within small function
int small_function(void) {
    if (error) goto cleanup;
    // 10-20 lines
cleanup:
    return;
}

// Bad: goto in 500-line function
// Control flow becomes incomprehensible

/* Best Practice 5: Initialize before goto */
// Bad:
if (error) goto cleanup;
FILE *fp = fopen(...);  // Jumped over!
cleanup:
    fclose(fp);  // fp uninitialized!

// Good:
FILE *fp = NULL;
if (error) goto cleanup;
fp = fopen(...);
cleanup:
    if (fp) fclose(fp);  // Safe

/* Best Practice 6: Limit labels per function */
// Maximum 2-3 labels per function
// More suggests refactoring needed`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Summary & What's Next</h2>
                <KeyFacts><ul>
                        <li>Goto jumps unconditionally to a label</li>
                        <li>Avoid goto in most code - use structured constructs</li>
                        <li>Legitimate uses: error cleanup, breaking nested loops</li>
                        <li>Linux kernel uses goto extensively for cleanup</li>
                        <li>Only jump forward and down, never backward</li>
                        <li>Document why goto is necessary</li>
                        <li>Consider alternatives: functions, early returns, flags</li>
                        <li>If you use goto, keep it simple and localized</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default CGoto;



